import React from "react";

/** Responsive section padding: 150px desktop, scaled down (matches Figma). */
const SECTION_PADDING_Y = "py-[clamp(60px,8vw,150px)]";

/** Section background (Figma charcoal). */
const SECTION_BG = "#191919";

/** Overlap fill color (used for middle "Both" block on mobile). */
const OVERLAP_BG = "#282D3D";

/** Venn circle stroke: matches VennDiagram.svg (stroke #6C84FA, 2px, dash 4 4). */
const VENN_STROKE_TOP = { borderBottom: 'none',
	borderLeft: '2px dashed #6C84FA',
	borderTop: '2px dashed #6C84FA',
	borderRight: '2px dashed #6C84FA' };

const VENN_STROKE_BOTTOM = { borderBottom: '2px dashed #6C84FA',
	borderLeft: '2px dashed #6C84FA',
	borderTop: 'none',
	borderRight: '2px dashed #6C84FA' };

const VENN_STROKE = { border: '2px dashed #6C84FA' };

/** VennDiagram.svg viewBox is 0 0 940 700. Circle centers at ~350 and 590 (x), vertical center 350.
 * We overlay three zones by percentage of container width:
 * - Left text: centered in left circle (center ~37% from left) → zone 5%–35%
 * - Center (overlap): 35%–65%
 * - Right text: centered in right circle (center ~63%) → zone 65%–95%
 */
const FitSection = () => {
	return (
		<div
			className={`FitSection text-white ${SECTION_PADDING_Y} px-2 xl:px-0 overflow-hidden relative`}
			style={{ backgroundColor: SECTION_BG }}
		>
			<div className="container mx-auto px-4 max-w-7xl relative z-10">
				{/* Top Headline — centered; "BOTH Sides of Your Mandate" larger/bolder per Figma */}
				<div className="text-center mb-[clamp(2rem,4vw,3.5rem)]">
					<h2 className="text-[clamp(22px,2.2vw,32px)] font-normal leading-[1.4] text-white">
						A Development Partner Built for{" "}<br />
						<span className="font-bold text-[clamp(26px,2.8vw,40px)]">BOTH Sides of Your Mandate</span>
					</h2>
				</div>

				{/* Desktop/Tablet: Venn graphic from SVG file with text overlaid in three zones */}
				<div className="hidden md:block relative w-full max-w-5xl mx-auto">
					{/* SVG as visual source of truth — scales with container, preserves aspect ratio */}
					<div className="relative w-full" style={{ aspectRatio: "940/700" }}>
						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img
							src="/images/VennDiagram.svg"
							alt=""
							className="absolute inset-0 w-full h-full object-contain object-center"
							aria-hidden
						/>

						{/* Left circle text — aligned with visual center of left circle (~37% from left) */}
						<div
							className="absolute inset-0 flex items-center justify-start pointer-events-none"
							style={{ left: "3%", width: "30%", top: 0, bottom: 0 }}
						>
							<p
								className="text-white font-normal leading-relaxed text-[clamp(16px,1.05vw,16px)] max-w-[200px] text-center"
								style={{ lineHeight: 1.55 }}
							>
								Designing software people love to use that is intuitive, sleek, and modern.
							</p>
						</div>

						{/* Overlap content — centered in lens (middle 30% of width, vertically centered) */}
						<div
							className="absolute inset-0 flex items-center justify-center pointer-events-none"
							style={{ left: "30%", right: "30%", top: 0, bottom: 0 }}
						>
							<div className="text-center w-full max-w-[240px] lg:max-w-[400px] px-4 py-5">
								<div className="mb-5">
									<p className="text-[clamp(16px,1.2vw,24px)] text-white font-normal leading-tight">
										Most partners can do one.
									</p>
									<p className="text-[clamp(16px,1.2vw,24px)] text-white font-bold leading-tight mt-1">
										Crafted does both.
									</p>
								</div>
								<ul className="text-left space-y-3 text-gray-200 text-[clamp(14px,0.95vw,16px)] leading-snug">
									<li className="flex items-start gap-2">
										<CheckIcon className="flex-shrink-0 mt-0.5 text-[#6BD0E0]" />
										<span>Delivering lovable UX with refined taste and creative sensitivity</span>
									</li>
									<li className="flex items-start gap-2">
										<CheckIcon className="flex-shrink-0 mt-0.5 text-[#6BD0E0]" />
										<span>
											Shipping faster than internal teams through disciplined execution + AI-native
											workflows
										</span>
									</li>
									<li className="flex items-start gap-2">
										<CheckIcon className="flex-shrink-0 mt-0.5 text-[#6BD0E0]" />
										<span>
											Supporting your roadmap as an integrated, long-term partner – not a transactional
											vendor
										</span>
									</li>
								</ul>
							</div>
						</div>

						{/* Right circle text — aligned with visual center of right circle (~63% from left) */}
						<div
							className="absolute inset-0 flex items-center justify-end pointer-events-none"
							style={{ left: "67%", width: "30%", top: 0, bottom: 0 }}
						>
							<p
								className="text-white font-normal leading-relaxed text-[clamp(16px,1.05vw,16px)] max-w-[200px] text-center"
								style={{ lineHeight: 1.55 }}
							>
								Shipping quality code faster with shorter release cycles.
							</p>
						</div>
					</div>
				</div>

				{/* Mobile (< 768px): stacked layout — Design → Both → Engineering; middle overlaps, dashed borders on outer blocks */}
				<div className="md:hidden flex flex-col w-full">
					{/* 1. Design block (left circle content) — section bg, dashed border, smaller padding */}
					<div
						className="w-full px-5 py-4 rounded-xl flex-shrink-0"
						style={{ backgroundColor: SECTION_BG, ...VENN_STROKE_TOP }}
					>
						<p className="text-white font-normal leading-relaxed text-sm text-center" style={{ lineHeight: 1.55 }}>
							Designing software people love to use that is intuitive, sleek, and modern.
						</p>
					</div>

					{/* 2. Highlighted "Both" block — overlap bg, no dashed border; z-index + negative margin so it stacks on top */}
					<div
						className="w-full px-5 py-6 rounded-xl relative z-10 flex-shrink-0"
						style={{ backgroundColor: OVERLAP_BG, ...VENN_STROKE }}
					>
						<div className="mb-5 text-center">
							<p className="text-base text-white font-normal leading-tight">
								Most partners can do one.
							</p>
							<p className="text-base text-white font-bold leading-tight mt-1">
								Crafted does both.
							</p>
						</div>
						<ul className="text-left space-y-3 text-gray-200 text-sm leading-snug">
							<li className="flex items-start gap-2">
								<CheckIcon className="flex-shrink-0 mt-0.5 text-[#6BD0E0]" />
								<span>Delivering lovable UX with refined taste and creative sensitivity</span>
							</li>
							<li className="flex items-start gap-2">
								<CheckIcon className="flex-shrink-0 mt-0.5 text-[#6BD0E0]" />
								<span>
									Shipping faster than internal teams through disciplined execution + AI-native
									workflows
								</span>
							</li>
							<li className="flex items-start gap-2">
								<CheckIcon className="flex-shrink-0 mt-0.5 text-[#6BD0E0]" />
								<span>
									Supporting your roadmap as an integrated, long-term partner – not a transactional
									vendor
								</span>
							</li>
						</ul>
					</div>

					{/* 3. Engineering block (right circle content) — section bg, dashed border, smaller padding */}
					<div
						className="w-full px-5 py-4 rounded-xl flex-shrink-0"
						style={{ backgroundColor: SECTION_BG, ...VENN_STROKE_BOTTOM }}
					>
						<p className="text-white font-normal leading-relaxed text-sm text-center" style={{ lineHeight: 1.55 }}>
							Shipping quality code faster with shorter release cycles.
						</p>
					</div>
				</div>

				{/* Bottom CTA — generous spacing above to match Figma */}
				<div className="text-center mt-[clamp(2.5rem,5vw,4rem)]">
					<h2 className="text-[clamp(22px,2.2vw,32px)] text-white font-normal leading-relaxed max-w-2xl mx-auto">
						If these are the outcomes you&apos;re responsible for,{" "}
						<span className="font-bold">we&apos;re the partner built for you.</span>
					</h2>
				</div>
			</div>
		</div>
	);
};

function CheckIcon({ className = "" }: { className?: string }) {
	return (
		<svg
			className={className}
			width="16"
			height="12"
			viewBox="0 0 16 12"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden
		>
			<path
				d="M1 6L5.5 10.5L14.5 1.5"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

export default FitSection;
